import { Company } from '../types/Company.type';

/**
 * The company flags legacy's dealer menu is gated on (lhphcapital-kdm6). A dealer sees the
 * Floorplans tab when FloorplanON is on, Borrowing Base when BorrowBase is on, and each
 * import on the flag Imports_N_M reads for it.
 *
 * ⚠️ UX, not security. The API reads the same flags off the caller's own company before an
 * upload (companyAuth.requireCompanyFlag), and scopes every read to that company.
 */
export type DealerCompanyFlags = Pick<
  Company,
  'FloorplanON' | 'BorrowBase' | 'CreditLineON' | 'BHPHOn' | 'IndirectOn' | 'HybridOn'
>;

export type DealerCompanyFlag = keyof DealerCompanyFlags;

export const DEALER_COMPANY_FLAGS: DealerCompanyFlag[] = [
  'FloorplanON',
  'BorrowBase',
  'CreditLineON',
  'BHPHOn',
  'IndirectOn',
  'HybridOn',
];

/** 1 or true. NULL — most dealers' BHPHOn, IndirectOn and HybridOn — is off, as in legacy. */
export function isFlagOn(value: unknown): boolean {
  return value === true || Number(value) === 1;
}

/** Any one of `flags` on. No flags loaded (null) is off. */
export function anyFlagOn(
  flags: DealerCompanyFlags | null | undefined,
  wanted: DealerCompanyFlag[]
): boolean {
  if (!flags) return false;
  return wanted.some(f => isFlagOn(flags[f]));
}

/** Only the flag columns, so nothing else from the company row is held for the menu. */
export function pickDealerCompanyFlags(company: Partial<Company> | null | undefined) {
  if (!company) return null;
  const flags = {} as Record<DealerCompanyFlag, unknown>;
  DEALER_COMPANY_FLAGS.forEach(f => (flags[f] = company[f] ?? null));
  return flags as DealerCompanyFlags;
}

/**
 * The dealer Imports menu, in legacy's order, each with the flag(s) that show it. Repo/Swaps
 * is legacy's `swap`: loanp + BHPH + indirect. The API's DEALER_IMPORT_FLAGS (imports.routes.js)
 * is the same table.
 */
export const DEALER_IMPORTS: { name: string; subpage: string; flags: DealerCompanyFlag[] }[] = [
  { name: 'Loan Requests', subpage: 'loan-requests', flags: ['CreditLineON'] },
  { name: 'Premier Loan Requests', subpage: 'premier-loan-requests', flags: ['HybridOn'] },
  { name: 'BHPH Loan Requests', subpage: 'bhph-loan-requests', flags: ['BHPHOn'] },
  { name: 'Indirect Loan Requests', subpage: 'indirect-loan-requests', flags: ['IndirectOn'] },
  { name: 'Floorplan Request', subpage: 'floorplan-request', flags: ['FloorplanON'] },
  {
    // lhphcapital-42bw: legacy's dealer RepoSwap import is the lender's, unchanged.
    name: 'Repos/Swaps',
    subpage: 'refi-swap-premier-conversion',
    flags: ['CreditLineON', 'BHPHOn', 'IndirectOn'],
  },
];

export function dealerImports(flags: DealerCompanyFlags | null | undefined) {
  return DEALER_IMPORTS.filter(i => anyFlagOn(flags, i.flags));
}
